import { FiChevronRight, FiPlus, FiThumbsUp } from 'react-icons/fi'
import { Link } from 'react-router-dom'
import type { WebinarSuggestion } from '@domain/models/webinar-suggestions'

interface SuggestionsUserLike {
  uid: string
  isAdmin: boolean
}

interface SuggestedWebinarCardProps {
  user: SuggestionsUserLike | null
  suggestions: WebinarSuggestion[]
  loading: boolean
  error: string | null
  actionError: string | null
  activeLikeId: string | null
  onCreate: () => void
  onToggleLike: (suggestion: WebinarSuggestion) => void
}

export default function SuggestedWebinarCard({
  user,
  suggestions,
  loading,
  error,
  actionError,
  activeLikeId,
  onCreate,
  onToggleLike,
}: SuggestedWebinarCardProps) {
  const topSuggestions = suggestions.slice(0, 2)

  return (
    <article className="rounded-xl border-2 border-blue bg-card-surface p-4 shadow-lg shadow-black/20">
      <div className="flex items-center justify-between gap-3">
        <Link to="/webinars/suggestions" className="no-underline">
          <h3 className="text-lg font-bold text-blue sm:text-xl">Suggested Webinars</h3>
        </Link>
        {user ? (
          <button
            type="button"
            onClick={onCreate}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-blue px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue/90"
          >
            <FiPlus className="size-4 text-white" aria-hidden="true" />
            Suggest
          </button>
        ) : null}
      </div>

      {error || actionError ? (
        <p className="mt-3 text-sm font-semibold text-text-secondary">{error ?? actionError}</p>
      ) : null}

      {loading ? (
        <p className="mt-4 text-sm font-semibold text-text-secondary">
          Loading suggested webinars...
        </p>
      ) : topSuggestions.length > 0 ? (
        <>
          <div className="mt-4 space-y-4">
            {topSuggestions.map((suggestion) => {
              const isLikePending = activeLikeId === suggestion.uid
              const liked = suggestion.likedByCurrentUser === true

              return (
                <article
                  key={suggestion.uid}
                  className="overflow-hidden rounded-xl border border-border-light bg-background"
                >
                  <div className="flex items-stretch">
                    <Link to="/webinars/suggestions" className="min-w-0 flex-1 p-4 no-underline">
                      <h4 className="wrap-break-word text-lg font-bold text-text-primary">
                        {suggestion.subject}
                      </h4>
                    </Link>
                    <div className="flex w-24 shrink-0 items-center justify-center border-l border-border-light bg-background px-2">
                      <button
                        type="button"
                        onClick={() => onToggleLike(suggestion)}
                        disabled={isLikePending}
                        className={`inline-flex shrink-0 items-center gap-2 rounded-full border border-blue px-3 py-2 text-sm font-semibold transition-colors ${
                          liked
                            ? 'bg-blue text-white'
                            : 'bg-surface text-blue hover:bg-blue/10'
                        } disabled:cursor-not-allowed disabled:opacity-60`}
                        aria-label="Toggle webinar suggestion like"
                      >
                        <FiThumbsUp className="h-4 w-4" />
                        {suggestion.likesCount}
                      </button>
                    </div>
                  </div>
                </article>
              )
            })}
          </div>
        </>
      ) : (
        <p className="mt-4 text-sm font-semibold text-text-secondary">
          No webinar suggestions yet. Share the next topic you would like to see.
        </p>
      )}

      <div className="mt-4 flex justify-center">
        <Link
          to="/webinars/suggestions"
          aria-label="See more suggested webinars"
          className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue px-4 py-3 text-center text-sm font-semibold leading-tight text-white no-underline shadow-sm transition-[transform,opacity,background-color] duration-200 hover:scale-[1.02] hover:bg-blue/90 hover:opacity-90 sm:text-base"
        >
          See more suggested webinars
          <FiChevronRight className="size-4" aria-hidden="true" />
        </Link>
      </div>
    </article>
  )
}
